import {
    Typography, Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    CircularProgress, Snackbar, Alert, InputAdornment
} from '@mui/material'
import { Search as SearchIcon, Add } from '@mui/icons-material'
import PhoneInput from 'react-phone-input-2'
import 'react-phone-input-2/lib/style.css'
import { FC, useCallback, useEffect, useState } from 'react'
import EmptyView from '../common/EmptyView'
import Show from '../common/show'
import { Api, buildPhone, numberWithCommas } from '../../helpers'
import { getCurrencyByPhone } from '../../helpers/countries'
import { Connect, mapStateToProps } from '../../Redux'
import { Search, SearchIconWrapper, StyledInputBase } from '../Home'
import _ from 'lodash'

interface Client {
    _id: string
    Username: string
    Phone: string
    earning: number
    totalEarning: number
    totalDevices: number
    dateJoined: string
}

interface FormState {
    Username: string
    phoneNumber: string
    dialCode: string
    Password: string
}

interface StateProps {
    clients: Client[]
    loading: boolean
    searchVal: string | null
    showModal: boolean
    saving: boolean
    form: FormState
    error: string | null
}

const defaultForm: FormState = {
    Username: '',
    phoneNumber: '',
    dialCode: '254',
    Password: ''
}

const headers = ['Username', 'Phone', 'Current Earning', 'Total Earnings', 'Total Tables', 'Date Joined']

const Clients: FC = () => {
    const [state, setState] = useState<StateProps>({
        clients: [],
        loading: true,
        searchVal: null,
        showModal: false,
        saving: false,
        form: defaultForm,
        error: null
    })
    const { clients, loading, searchVal, showModal, saving, form, error } = state

    const setForm = (patch: Partial<FormState>) =>
        setState(s => ({ ...s, form: { ...s.form, ...patch } }))

    const getClients = ({ searchVal }: { searchVal?: string | null } = {}) => {
        Api({
            controller: 'Users',
            action: searchVal ? 'searchClients' : 'getClients',
            searchVal
        }).then((resp: any) => {
            setState(s => ({ ...s, clients: resp, loading: false, searchVal: searchVal ?? null }))
        }).catch(() => {
            setState(s => ({ ...s, loading: false }))
        })
    }

    const searchClients = (val: string) => getClients({ searchVal: val })
    const startSearch = useCallback(_.debounce(searchClients, 1000), [])

    const saveClient = () => {
        const phone = buildPhone(form.dialCode, form.phoneNumber)
        if (!phone) {
            setState(s => ({ ...s, error: 'Enter a valid phone number.' }))
            return
        }
        setState(s => ({ ...s, saving: true }))
        Api({
            controller: 'Users',
            action: 'addClient',
            Username: form.Username,
            Phone: phone,
            Password: form.Password,
            Status: 'active'
        }).then((resp: any) => {
            if (resp.error)
                setState(s => ({ ...s, saving: false, error: resp.error.message }))
            else {
                setState(s => ({ ...s, saving: false, showModal: false, form: defaultForm }))
                getClients()
            }
        }).catch((err) => {
            setState(s => ({ ...s, saving: false, error: err?.message || 'Something went wrong. Please try again.' }))
        })
    }

    useEffect(() => {
        getClients()
    }, [])

    return (
        <Box>
            <Box sx={{ padding: 2, textAlign: 'left' }}>
                <Typography sx={{ color: '#000', fontSize: 25, fontWeight: 'bold' }}>Clients</Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', px: '5px' }}>
                <Search sx={{ borderRadius: 3, flex: 1, margin: 0 }}>
                    <SearchIconWrapper>
                        <SearchIcon sx={{ fontSize: 20, color: 'black' }} />
                    </SearchIconWrapper>
                    <StyledInputBase
                        placeholder='Search'
                        inputProps={{ 'aria-label': 'search' }}
                        onChange={(e) => startSearch(e.target.value)}
                    />
                </Search>
                <Button
                    variant='contained'
                    startIcon={<Add />}
                    onClick={() => setState(s => ({ ...s, showModal: true, form: defaultForm }))}
                    sx={{ borderRadius: 2, whiteSpace: 'nowrap', ml: 1 }}
                >
                    Add Client
                </Button>
            </Box>
            <Show>
                <Show.When isTrue={loading || !!(searchVal && !clients.length)}>
                    <EmptyView
                        loading={loading}
                        oopTxt='No Clients'
                        text='No clients found.'
                    />
                </Show.When>
                <Show.Else>
                    <TableContainer component={Paper} sx={{ mt: 2, mx: '5px' }}>
                        <Table>
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'primary.main' }}>
                                    {headers.map(h => (
                                        <TableCell key={h} sx={{ color: 'white', fontWeight: 'bold' }}>{h}</TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {clients.map(({ _id, Username, Phone, earning, totalEarning, totalDevices, dateJoined }) => (
                                    <TableRow key={_id} hover>
                                        <TableCell>{Username}</TableCell>
                                        <TableCell>{Phone}</TableCell>
                                        <TableCell>{getCurrencyByPhone(Phone)} {numberWithCommas(earning)}</TableCell>
                                        <TableCell>{getCurrencyByPhone(Phone)} {numberWithCommas(totalEarning)}</TableCell>
                                        <TableCell>{totalDevices}</TableCell>
                                        <TableCell>{new Date(dateJoined).toLocaleDateString()}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Show.Else>
            </Show>

            <Dialog open={showModal} onClose={() => setState(s => ({ ...s, showModal: false }))} fullWidth maxWidth='sm'>
                <DialogTitle sx={{ fontWeight: 'bold' }}>Add Client</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
                    <TextField
                        label='Username'
                        value={form.Username}
                        onChange={e => setForm({ Username: e.target.value })}
                        fullWidth
                    />
                    <TextField
                        label='Phone Number'
                        value={form.phoneNumber}
                        onChange={e => setForm({ phoneNumber: e.target.value })}
                        fullWidth
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position='start'>
                                    <PhoneInput
                                        country={'ke'}
                                        value={form.dialCode}
                                        onChange={(_value, country: any) => { if (country?.dialCode) setForm({ dialCode: country.dialCode }) }}
                                        inputStyle={{ border: 'none', outline: 'none', width: 85, backgroundColor: 'transparent' }}
                                        dropdownStyle={{ top: 'auto', bottom: 0, display: 'flex', flexDirection: 'column', alignItems: 'start', width: 380 }}
                                        buttonStyle={{ backgroundColor: 'transparent', border: 'none', transition: 'none' }}
                                    />
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        label='Password'
                        type='password'
                        value={form.Password}
                        onChange={e => setForm({ Password: e.target.value })}
                        fullWidth
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setState(s => ({ ...s, showModal: false }))} disabled={saving}>Cancel</Button>
                    <Button
                        variant='contained'
                        onClick={saveClient}
                        disabled={saving || !form.Username || !form.phoneNumber || !form.Password}
                        startIcon={saving ? <CircularProgress size={16} color='inherit' /> : undefined}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={Boolean(error)}
                autoHideDuration={4000}
                onClose={() => setState(s => ({ ...s, error: null }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert severity='error' onClose={() => setState(s => ({ ...s, error: null }))} sx={{ width: '100%' }}>
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    )
}

export default Connect(mapStateToProps)(Clients)
